import ActionButton from "@/components/buttons/ActionButton";
import AntIcon from "@/components/icons/AntIcon";
import { selectStandardProps } from "@/utils/componentUtils";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validations } from "@/utils/validationUtils";
import { Button, Card, Flex, Form, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { rowGutter } from "../../../../common/constants";
import { AGENT_FILTER_INCLUSION_DEFAULTS, DEFAULT_FILTER_AGENT_COMPETENCES_QUERY_PARAMS } from "../../api";
import { AgentCompetenceLevel, AgentCompetenceProvider, AgentCompetenceType, FinancialSector } from "../../enums";
import { AgentCompetenceFilterPageRequest } from "../../types";
import { AgentCommonFilterFields } from "../commonfilters/AgentCommonFilterFields";
import { AgentExtendedFilterModalForm } from "../commonfilters/AgentExtendedFilterModalForm";

type ExtendedFilter = { completedEducationDateRange?: string[]; completedAcademyDateRange?: string[] };

interface Props {
  searchParams: AgentCompetenceFilterPageRequest;
}

const AgentEndingCompetenceFilterForm = ({ searchParams }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form] = Form.useForm<AgentCompetenceFilterPageRequest & ExtendedFilter>();

  useEffect(() => {
    form.setFieldsValue({
      ...searchParams,
      completedEducationDateRange:
        searchParams.completedEducationDateMin && searchParams.completedEducationDateMax
          ? [searchParams.completedEducationDateMin, searchParams.completedEducationDateMax]
          : undefined,
      completedAcademyDateRange:
        searchParams.completedAcademyDateMin && searchParams.completedAcademyDateMax
          ? [searchParams.completedAcademyDateMin, searchParams.completedAcademyDateMax]
          : undefined,
    });
  }, [searchParams]);

  const handleFilterSubmit = ({
    completedEducationDateRange,
    completedAcademyDateRange,
    ...filter
  }: AgentCompetenceFilterPageRequest & ExtendedFilter) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...filter,
            ...skipInitialFormValues(filter, DEFAULT_FILTER_AGENT_COMPETENCES_QUERY_PARAMS),
            pageIndex: undefined,
          },
        }),
      },
      { replace: true },
    );
  };

  const handleFilterClear = () => {
    form.resetFields();
    handleFilterSubmit(form.getFieldsValue());
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("agent.titles.endingCompetences")}</h2>}
      extra={
        <ActionButton color="danger" size="middle" variant="outlined" icon="reload" onClick={handleFilterClear}>
          {t("common.resetFilter")}
        </ActionButton>
      }
    >
      <Form
        form={form}
        initialValues={AGENT_FILTER_INCLUSION_DEFAULTS.ALL_INCLUDED}
        layout="vertical"
        onFinish={handleFilterSubmit}
        name="agentEndingCompetenceFilterForm"
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(4, 1fr)`,
            gap: rowGutter,
            alignItems: "end",
          }}
          className="min-width-0-children"
        >
          <AgentCommonFilterFields>
            <Form.Item name="types" label={t("agent.competence.filter.types")} rules={[validations.none]}>
              <Select
                {...selectStandardProps}
                allowClear
                mode="multiple"
                maxTagCount="responsive"
                options={Object.keys(AgentCompetenceType).map((type) => ({
                  value: type,
                  label: t("agent.competence.enums.competenceType." + type),
                }))}
              />
            </Form.Item>

            <Form.Item name="sectors" label={t("agent.competence.filter.sectors")} rules={[validations.none]}>
              <Select
                {...selectStandardProps}
                allowClear
                mode="multiple"
                maxTagCount="responsive"
                options={Object.keys(FinancialSector).map((sector) => ({
                  value: sector,
                  label: t("agent.enums.financialSector." + sector),
                }))}
              />
            </Form.Item>

            <Form.Item name="levels" label={t("agent.competence.filter.levels")} rules={[validations.none]}>
              <Select
                {...selectStandardProps}
                allowClear
                mode="multiple"
                maxTagCount="responsive"
                options={Object.keys(AgentCompetenceLevel).map((level) => ({
                  value: level,
                  label: t("agent.competence.enums.competenceLevel." + level),
                }))}
              />
            </Form.Item>

            <Form.Item name="providers" label={t("agent.competence.filter.providers")} rules={[validations.none]}>
              <Select
                {...selectStandardProps}
                allowClear
                mode="multiple"
                maxTagCount="responsive"
                options={Object.keys(AgentCompetenceProvider).map((provider) => ({
                  value: provider,
                  label: t("agent.competence.enums.competenceProvider." + provider),
                }))}
              />
            </Form.Item>
          </AgentCommonFilterFields>
        </div>

        <Flex justify="space-between">
          <AgentExtendedFilterModalForm
            onSubmit={(values) => {
              form.setFieldsValue(values);
              handleFilterSubmit(form.getFieldsValue());
            }}
          />

          <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
            {t("common.filter")}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
};

export default AgentEndingCompetenceFilterForm;
